import Phaser from 'phaser';
import type { GameState } from '../types';
import { COLORS } from '../main';

export interface StatusBarOptions {
  x: number;
  y: number;
  width: number;
  height: number;
  onRoll: () => void;
  onFinishPlacing: () => void;
  onEndTurn: () => void;
  onMenu: () => void;
}

export class StatusBar {
  scene: Phaser.Scene;
  container: Phaser.GameObjects.Container;
  options: StatusBarOptions;

  constructor(scene: Phaser.Scene, options: StatusBarOptions) {
    this.scene = scene;
    this.options = options;
    this.container = scene.add.container(options.x, options.y);
    const bg = scene.add.rectangle(
      options.width / 2,
      options.height / 2,
      options.width,
      options.height,
      COLORS.panel
    );
    bg.setStrokeStyle(1, COLORS.border);
    this.container.add(bg);
  }

  refresh(state: GameState): void {
    const bg = this.container.getAt(0);
    this.container.removeAll(false);
    if (bg) this.container.add(bg);

    const { width, height } = this.options;
    // Bag composition
    const counts = {
      white: state.bag.filter((d) => d.color === 'white').length,
      red: state.bag.filter((d) => d.color === 'red').length,
      blue: state.bag.filter((d) => d.color === 'blue').length,
    };
    const tokens = state.tokens;

    const bagText = this.scene.add
      .text(
        12,
        height / 2,
        `Bag: ${state.bag.length}  W:${counts.white} R:${counts.red} B:${counts.blue}`,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#e6edf3',
        }
      )
      .setOrigin(0, 0.5);

    const tokenText = this.scene.add
      .text(
        12,
        height / 2 + 14,
        `Tokens — ↻ reroll:${tokens.reroll}  ⇄ convert:${tokens.convertColor}`,
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#9ca3af',
        }
      )
      .setOrigin(0, 0.5);

    this.container.add([bagText, tokenText]);

    // Action button (right side) varies by phase
    const btnW = 110;
    const btnH = 36;
    const btnX = width - btnW / 2 - 10;
    const btnY = height / 2;
    let label: string | null = null;
    let cb: (() => void) | null = null;
    let enabled = true;

    switch (state.phase) {
      case 'Drawing':
        label = 'Draw';
        cb = () => this.options.onRoll(); // 'Draw' triggers startTurn upstream
        break;
      case 'Rolling':
        label = 'Roll dice';
        cb = this.options.onRoll;
        break;
      case 'AdjustingPerks':
      case 'Placing':
        label = state.hand.length === 0 ? 'Done placing' : `Place ${state.hand.length}`;
        cb = this.options.onFinishPlacing;
        enabled = state.hand.length === 0;
        break;
      case 'Resolving':
        label = 'End turn';
        cb = this.options.onEndTurn;
        break;
      case 'TurnEnd':
      case 'Win':
      case 'Loss':
        label = 'Menu';
        cb = this.options.onMenu;
        break;
    }

    if (label && cb) {
      const fill = enabled ? COLORS.accent : COLORS.cardAcquired;
      const rect = this.scene.add
        .rectangle(btnX, btnY, btnW, btnH, fill)
        .setStrokeStyle(2, COLORS.border);
      if (enabled) {
        rect.setInteractive({ useHandCursor: true }).on('pointerdown', cb);
      }
      const txt = this.scene.add
        .text(btnX, btnY, label, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          fontStyle: 'bold',
          color: '#0d1117',
        })
        .setOrigin(0.5);
      this.container.add([rect, txt]);
    }
  }
}
